import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";

// Pages where Crisp chat should be hidden
const HIDDEN_PATHS = ["/login", "/share"];

/**
 * Check if Crisp is loaded and ready to accept commands
 */
function isCrispReady(): boolean {
  return typeof window.$crisp !== "undefined" && Array.isArray(window.$crisp);
}

/**
 * Crisp Chat Component
 *
 * Loads and initializes the Crisp chat widget for customer support.
 * Features:
 * - Only loads if VITE_CRISP_WEBSITE_ID is configured
 * - Identifies authenticated users (email, name) for better support
 * - Hides on login and shared report pages
 * - Handles React StrictMode double-mount
 */
export function CrispChat() {
  const { user, isAuthenticated } = useAuth0();
  const location = useLocation();
  const isInitialized = useRef(false);

  // Check if we should hide on this path
  const shouldHide = HIDDEN_PATHS.some((path) => location.pathname.startsWith(path));

  // Initialize Crisp script (only once)
  useEffect(() => {
    const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;

    // Only load Crisp if website ID is configured
    if (!crispWebsiteId) {
      return;
    }

    // Prevent double initialization in React StrictMode
    // Check if script is already loaded or we've already initialized
    if (isInitialized.current || document.getElementById("crisp-script")) {
      return;
    }

    // Initialize Crisp
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = crispWebsiteId;

    // Load Crisp script dynamically
    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    script.id = "crisp-script";
    document.head.appendChild(script);

    isInitialized.current = true;

    // No cleanup - Crisp should persist across the session
    // Removing it would lose chat history and reconnection state
  }, []);

  // Identify user when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user || !isCrispReady()) {
      return;
    }

    // Set user email for identification
    if (user.email) {
      window.$crisp.push(["set", "user:email", user.email]);
    }

    // Set user nickname (full name or fallback to email prefix)
    const nickname = user.name || user.nickname || user.email?.split("@")[0];
    if (nickname) {
      window.$crisp.push(["set", "user:nickname", nickname]);
    }

    // Set user avatar if available
    if (user.picture) {
      window.$crisp.push(["set", "user:avatar", user.picture]);
    }

    // Add custom session data for support context
    window.$crisp.push([
      "set",
      "session:data",
      [
        ["auth0_sub", user.sub || ""],
        ["email_verified", String(user.email_verified || false)],
      ],
    ]);
  }, [isAuthenticated, user]);

  // Show/hide chat based on current route
  useEffect(() => {
    if (!isCrispReady()) {
      return;
    }

    if (shouldHide) {
      window.$crisp.push(["do", "chat:hide"]);
    } else {
      window.$crisp.push(["do", "chat:show"]);
    }
  }, [shouldHide]);

  return null;
}

// TypeScript declarations for Crisp globals
declare global {
  interface Window {
    $crisp: CrispCommand[];
    CRISP_WEBSITE_ID?: string;
  }
}

// Crisp command types for better type safety
type CrispCommand =
  | ["set", "user:email", string]
  | ["set", "user:nickname", string]
  | ["set", "user:avatar", string]
  | ["set", "session:data", [string, string][]]
  | ["do", "chat:hide"]
  | ["do", "chat:show"]
  | ["do", "chat:open"]
  | ["do", "chat:close"]
  | ["on", string, () => void];
